import classNames from 'classnames';
import { CSSProperties, FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

import {
	Box,
	Button,
	Drawer,
	DrawerProps,
	Flex,
	StyleProp,
	TextInput,
	TextInputProps,
} from '@mantine/core';

import IconArrowLeft from '../../../assets/icons/IconArrowLeft.svg?react';
import IconSearch from '../../../assets/icons/IconSearch.svg?react';
import IconX from '../../../assets/icons/IconX.svg?react';
import { CustomButtonProps } from '../../../types';

import classes from './BottomSheet.module.css';

type BottomSheetProps = {
	buttons?: CustomButtonProps[];
	buttonsWidget?: ReactNode;
	buttonsDirection?: StyleProp<CSSProperties['flexDirection']>;
	withBackButton?: boolean;
	search?: TextInputProps;
	navbar?: ReactNode;
	showHeader?: boolean;
	isFullScreen?: boolean;
	bgOpacity?: number;
	bgBlur?: number;
	isSwipeable?: boolean;
} & DrawerProps;

const config = {
	delta: 0,
	preventScrollOnSwipe: false,
	trackTouch: true,
	trackMouse: false,
	rotationAngle: 0,
	swipeDuration: Infinity,
	touchEventOptions: { passive: true },
};

export const BottomSheet: FC<BottomSheetProps> = ({
	buttons,
	buttonsWidget,
	children,
	withCloseButton = true,
	withBackButton = true,
	showHeader = true,
	isFullScreen = false,
	isSwipeable = true,
	buttonsDirection,
	search,
	navbar,
	title,
	bgOpacity = 0.5,
	bgBlur = 0,
	...others
}) => {
	// const changeThemeColor = useChangeThemeColor();

	// useEffect(() => {
	// 	changeThemeColor(others.opened ? '#7e7e7e' : undefined);
	// }, []);

	const bodyRef = useRef<HTMLDivElement | null>(null);
	const [isSwiping, setIsSwiping] = useState(false);
	const [delta, setDelta] = useState(0);

	const handlers = useSwipeable({
		onSwiping: (eventData) => {
			const { dir, deltaY } = eventData;

			setIsSwiping(true);

			if (dir === 'Down') {
				setDelta(-deltaY);
			}
		},
		onSwiped: (eventData) => {
			const { dir, deltaY } = eventData;

			setIsSwiping(false);

			if (dir === 'Down' && deltaY > 160 && bodyRef.current?.scrollTop === 0) {
				others.onClose();
			}
		},
		...config,
	});

	useEffect(() => {
		if (!isSwiping && delta < 160) setTimeout(() => setDelta(0), others.opened ? 0 : 500);
	}, [delta, isSwiping, others.opened]);

	return (
		<Drawer.Root position="bottom" trapFocus={false} {...others}>
			<Drawer.Overlay blur={bgBlur} backgroundOpacity={bgOpacity} />
			<Drawer.Content
				className={!isFullScreen ? classes.drawerContent : ''}
				mb={delta}
				// opacity={-(delta * 0.4) / 100}
				style={
					isFullScreen
						? {
								height: '100%',
								display: 'flex',
								overflowY: 'auto',
								flexDirection: 'column',
								borderRadius: '24px 24px 0 0',
								width: '100%',
								// top: 52,
							}
						: {}
				}
				{...(isSwipeable ? { ...handlers } : {})}
			>
				{showHeader && (
					<Drawer.Header className={classes.header}>
						<Flex direction="column" className={classes.headerContainer}>
							{isSwipeable && <Box className={classes.control} />}
							<Flex
								className={classNames(classes.headerContainer, {
									[classes.headerTitle]: search || navbar,
								})}
							>
								{withBackButton ? <IconArrowLeft width={24} onClick={others.onClose} /> : <Box />}
								<Drawer.Title className={classes.drawerTitle}>
									{/*TODO привеси заголовок к одному стилю и избавиться от ReactNode*/}
									{title}
								</Drawer.Title>
								{withCloseButton ? <IconX width={24} onClick={others.onClose} /> : <Box />}
							</Flex>
							{(navbar || search) && (
								<Flex direction="column" gap={16} w={'100%'}>
									{search && (
										<TextInput
											placeholder="Поиск"
											size="sm"
											w="100%"
											radius="13px"
											leftSection={<IconSearch width={20} />}
											{...search}
										/>
									)}

									{navbar}
								</Flex>
							)}
						</Flex>
					</Drawer.Header>
				)}
				<Drawer.Body className={classes.drawerBody} pb={0}>
					<Flex direction="column" className={isFullScreen ? classes.drawerBodyWrapper : ''}>
						<Box
							ref={bodyRef}
							className={classes.drawerBodyContent}
							style={
								isFullScreen
									? {
											// height: 'calc(100% - 52px)',
											height: '100%',
											flex: '1 1 auto',
										}
									: {}
							}
							mb={!buttons ? 34 : {}}
						>
							{children}
						</Box>
						{buttons && (
							<Flex className={classes.drawerBodyButtonsWrapper} direction={buttonsDirection}>
								{buttonsWidget}
								{buttons?.map((el, index) => (
									<Button {...el} size="lg" fullWidth key={index}>
										{el.children}
									</Button>
								))}
							</Flex>
						)}
					</Flex>
				</Drawer.Body>
			</Drawer.Content>
		</Drawer.Root>
	);
};
